import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Add auth header to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors — clear token but let React auth state handle redirect
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
    }
    return Promise.reject(error);
  }
);

// Helper function to format error messages
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  googleCallback: (session_id) => api.post('/auth/google-callback', { session_id }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me')
};

// User APIs
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`)
};

// Organization APIs
export const orgAPI = {
  getOrganizations: () => api.get('/organizations'),
  getOrganization: (id) => api.get(`/organizations/${id}`),
  createOrganization: (data) => api.post('/organizations', data),
  updateOrganization: (id, data) => api.put(`/organizations/${id}`, data),
  getInvoiceSequences: (orgId) => api.get(`/organizations/${orgId}/invoice-sequences`),
  createInvoiceSequence: (orgId, data) => api.post(`/organizations/${orgId}/invoice-sequences`, data),
  getNextInvoiceNumber: (orgId, seqId) => api.post(`/organizations/${orgId}/invoice-sequences/${seqId}/next`),
  previewInvoiceNumber: (orgId) => api.get(`/organizations/${orgId}/invoice-preview`),
};

// Ticket APIs
export const ticketAPI = {
  getTickets: (params) => api.get('/tickets', { params }),
  getTicket: (id) => api.get(`/tickets/${id}`),
  createTicket: (data) => api.post('/tickets', data),
  updateTicket: (id, data) => api.put(`/tickets/${id}`, data),
  addComment: (id, content) => api.post(`/tickets/${id}/comments`, { content }),
  checkSimilar: (data) => api.post('/tickets/check-similar', data),
  moveToDevelopment: (id, data) => api.post(`/tickets/${id}/move-to-development`, data)
};

// Client APIs
export const clientAPI = {
  getClients: () => api.get('/clients'),
  getClient: (id) => api.get(`/clients/${id}`),
  createClient: (data) => api.post('/clients', data),
  updateClient: (id, data) => api.put(`/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/clients/${id}`),
  getProjectBusinessMap: () => api.get('/project-business-map'),
};

// Project APIs
export const projectAPI = {
  getProjects: (clientId) => api.get('/projects', { params: clientId ? { client_id: clientId } : {} }),
  getProject: (id) => api.get(`/projects/${id}`),
  createProject: (data) => api.post('/projects', data),
  updateProject: (id, data) => api.put(`/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/projects/${id}`),
  getMembers: (id) => api.get(`/projects/${id}/members`),
  updateMembers: (id, memberIds, pmId) => api.put(`/projects/${id}/members`, { member_ids: memberIds, pm_id: pmId || null }),
};

// Application APIs
export const applicationAPI = {
  getApplications: (params) => api.get('/applications', { params }),
  getApplication: (id) => api.get(`/applications/${id}`),
  createApplication: (data) => api.post('/applications', data),
  updateApplication: (id, data) => api.put(`/applications/${id}`, data),
  deleteApplication: (id) => api.delete(`/applications/${id}`)
};

// Work Items APIs
export const workItemAPI = {
  getWorkItems: (params) => api.get('/work-items', { params })
};

// Subtask APIs
export const subtaskAPI = {
  getSubtasks: (taskId) => api.get(`/tasks/${taskId}/subtasks`),
  createSubtask: (taskId, data) => api.post(`/tasks/${taskId}/subtasks`, data),
  updateSubtask: (subtaskId, data) => api.put(`/subtasks/${subtaskId}`, data),
  deleteSubtask: (subtaskId) => api.delete(`/subtasks/${subtaskId}`),
  getSubtaskHistory: (subtaskId) => api.get(`/subtasks/${subtaskId}/history`),
};

// Reports APIs
export const reportAPI = {
  getResourceUtilization: (month) => api.get('/reports/resource-utilization', { params: month ? { month } : {} }),
  getProjectBilling: (month) => api.get('/reports/project-billing', { params: month ? { month } : {} }),
  getPendingApprovals: () => api.get('/tasks/pending-approval'),
  approveTask: (taskId, status) => api.post(`/tasks/${taskId}/approve`, { status }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getActivityStats: (params) => api.get('/dashboard/activity-stats', { params }),
  getTrends: (days, scope) => api.get('/dashboard/trends', { params: { days, scope } }),
  getLeaderboard: (scope) => api.get('/dashboard/leaderboard', { params: { scope } }),
  getSprintStats: (params) => api.get('/dashboard/sprint-stats', { params }),
  getSprintUserStats: (params) => api.get('/dashboard/sprint-user-stats', { params }),
};

// Sprint APIs
export const sprintAPI = {
  getSprints: (params) => api.get('/sprints', { params }),
  createSprint: (data) => api.post('/sprints', data),
  updateSprint: (id, data) => api.put(`/sprints/${id}`, data),
  deleteSprint: (id) => api.delete(`/sprints/${id}`),
  getPendingTasks: (id) => api.get(`/sprints/${id}/pending-tasks`),
  completeSprint: (id, data) => api.post(`/sprints/${id}/complete`, data),
};

// Task APIs
export const taskAPI = {
  getTasks: (params) => api.get('/tasks', { params }),
  getActivities: (params) => api.get('/tasks/activities', { params }),
  createTask: (data) => api.post('/tasks', data),
  updateTask: (id, data) => api.put(`/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  archiveTask: (id) => api.post(`/tasks/${id}/archive`),
  unarchiveTask: (id) => api.post(`/tasks/${id}/unarchive`),
  getComments: (taskId) => api.get(`/tasks/${taskId}/comments`),
  addComment: (taskId, content) => api.post(`/tasks/${taskId}/comments`, { content }),
  getHistory: (taskId) => api.get(`/tasks/${taskId}/history`),
  getResourceTimeline: () => api.get('/tasks/resource-timeline'),
  checkOverlaps: (data) => api.post('/tasks/check-overlaps', data),
  exportExcel: (params) => api.get('/tasks/export', { params, responseType: 'blob' }),
};

// Saved Views APIs
export const viewsAPI = {
  getViews: () => api.get('/saved-views'),
  createView: (data) => api.post('/saved-views', data),
  deleteView: (id) => api.delete(`/saved-views/${id}`),
};

// AI APIs
export const aiAPI = {
  getTaskSuggestions: (data) => api.post('/ai/task-suggestions', data),
  enhanceText: (data) => api.post('/ai/enhance-text', data),
  getTaskSummary: (taskId) => api.post('/ai/task-summary', { task_id: taskId }),
  getDashboardInsights: () => api.get('/ai/dashboard-insights'),
};

// Settings APIs
export const settingsAPI = {
  getLabels: () => api.get('/settings/labels'),
  createLabel: (data) => api.post('/settings/labels', data),
  updateLabel: (id, data) => api.put(`/settings/labels/${id}`, data),
  deleteLabel: (id) => api.delete(`/settings/labels/${id}`),
  getModules: () => api.get('/settings/modules'),
  createModule: (data) => api.post('/settings/modules', data),
  updateModule: (id, data) => api.put(`/settings/modules/${id}`, data),
  deleteModule: (id) => api.delete(`/settings/modules/${id}`),
  getStatuses: () => api.get('/settings/statuses'),
  createStatus: (data) => api.post('/settings/statuses', data),
  updateStatus: (id, data) => api.put(`/settings/statuses/${id}`, data),
};

// Task share APIs
export const shareAPI = {
  generateLink: (taskId) => api.post(`/tasks/${taskId}/share`),
  getShared: (token) => api.get(`/shared/${token}`),
};

// Task attachment APIs
export const attachmentAPI = {
  getAttachments: (taskId) => api.get(`/tasks/${taskId}/attachments`),
  uploadFile: (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${taskId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  addLink: (taskId, data) => api.post(`/tasks/${taskId}/attachments/link`, data),
  deleteAttachment: (taskId, attachmentId) => api.delete(`/tasks/${taskId}/attachments/${attachmentId}`),
};

// User project assignment APIs
export const userProjectAPI = {
  assignProjects: (userId, projectIds) => api.put(`/users/${userId}/projects`, { project_ids: projectIds }),
  getProjects: (userId) => api.get(`/users/${userId}/projects`),
};

// Notification APIs
export const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Gantt APIs
export const ganttAPI = {
  getGanttData: (projectId) => api.get('/gantt', { params: { project_id: projectId } })
};

// File APIs
export const fileAPI = {
  upload: (formData, ticketId = null) => {
    const url = ticketId ? `/files/upload?ticket_id=${ticketId}` : '/files/upload';
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileInfo: (fileId) => api.get(`/files/${fileId}`),
  getTicketFiles: (ticketId) => api.get(`/files/ticket/${ticketId}`),
  delete: (fileId) => api.delete(`/files/${fileId}`),
  linkToTicket: async (fileId, ticketId) => {
    // This would update the file's ticket_id in the backend
    // For now, we'll upload with ticket_id directly
    return { data: { success: true } };
  },
  getDownloadUrl: (fileId) => {
    const token = localStorage.getItem('access_token');
    return `${API}/files/${fileId}/download?auth=${token}`;
  }
};

export default api;